import type { CanvasDraftCheckpointReceipt, CanvasDraftControllerSnapshot } from './canvasDraftController';

/**
 * Identity captured before a user asks for an irreversible or durable Canvas
 * outcome. A later editor remount, draft replacement or keystroke makes the
 * original gesture stale even if an older checkpoint succeeds.
 */
export interface CanvasDurableBoundary {
  readonly mountEpoch: number;
  readonly draftId: string;
  readonly version: number;
}

export function captureCanvasDurableBoundary(input: {
  readonly mountEpoch: number;
  readonly draft: Pick<CanvasDraftControllerSnapshot, 'draftId' | 'version'>;
}): CanvasDurableBoundary | null {
  if (!input.draft.draftId) return null;
  return {
    mountEpoch: input.mountEpoch,
    draftId: input.draft.draftId,
    version: input.draft.version,
  };
}

/**
 * A checkpoint receipt proves bytes persisted at one point in time. It alone
 * cannot authorize an irreversible action if the visible Canvas moved while
 * waiting for it; compare it with the identity captured at the user gesture.
 */
export function checkpointMatchesCanvasDurableBoundary(
  boundary: CanvasDurableBoundary,
  input: Pick<CanvasDraftCheckpointReceipt, 'draftId' | 'version' | 'attemptId'> & {
    readonly liveMountEpoch: number;
    readonly liveDraft: Pick<CanvasDraftControllerSnapshot, 'draftId' | 'version' | 'savedVersion'>;
  },
): boolean {
  return typeof input.attemptId === 'string'
    && input.attemptId.length > 0
    && input.draftId === boundary.draftId
    && input.version === boundary.version
    && canvasDurableBoundaryIsCurrent(boundary, input);
}

/**
 * Recheck the visible owner after every async export stage. This deliberately
 * includes draftId: restoreDraft can swap the durable document without changing
 * either mountEpoch or version.
 */
export function canvasDurableBoundaryIsCurrent(
  boundary: CanvasDurableBoundary,
  input: {
    readonly liveMountEpoch: number;
    readonly liveDraft: Pick<CanvasDraftControllerSnapshot, 'draftId' | 'version' | 'savedVersion'>;
  },
): boolean {
  return input.liveMountEpoch === boundary.mountEpoch
    && input.liveDraft.draftId === boundary.draftId
    && input.liveDraft.version === boundary.version
    && input.liveDraft.savedVersion >= boundary.version;
}
